import type { Extracted, NormalizedItem } from "./types";

/**
 * Single source of truth for "which rows are visible/actionable", shared by the panel (render) and the
 * parent (keyboard nav). Keeping the predicate in one place guarantees the keyboard highlight can never
 * point at a row the panel didn't render — the manual "mirror this exactly" contract is gone.
 */

/** Rows visible under the "Selected only" segment (opt-in). Group headers always stay (dropped later only when empty). */
export const filterBySegment = <T>(
	items: NormalizedItem<T>[],
	showSelectedOnly: boolean | undefined,
	segment: "all" | "selected",
	effectiveIds: Set<string>,
): NormalizedItem<T>[] => {
	if (!showSelectedOnly || segment === "all") return items;
	return items.filter((item) => !!item.groupValues || effectiveIds.has(item.id));
};

/** Actionable option ids in render order: excludes group headers AND disabled rows (neither is ever toggled). */
export const toOptionIds = <T>(items: NormalizedItem<T>[]): string[] => items.filter((item) => !item.groupValues && !item.disabled).map((item) => item.id);

/**
 * Rows left after hiding the children of collapsed group headers (opt-in `collapsibleGroups`).
 *
 * Collapse is a DISPLAY concern, not a filter, so it is applied only to what the panel paints and what the
 * keyboard walks. Group-header state, a header's own select-all and the panel's "select all" deliberately keep
 * seeing a collapsed group's children, so folding a section never moves a checkbox. Headers themselves always
 * survive: dropping a header with no visible children is the search filter's job, and doing it for a collapsed
 * group is what made the group vanish instead of collapse (APP-725).
 */
export const filterCollapsedGroups = <T>(
	items: NormalizedItem<T>[],
	collapsedKeys: ReadonlySet<string>,
	idOf: (value: Extracted<T>) => string,
): NormalizedItem<T>[] => {
	if (collapsedKeys.size === 0) return items;
	const hiddenIds = new Set<string>();
	for (const item of items) {
		if (!item.groupValues || !collapsedKeys.has(item.key)) continue;
		for (const value of item.groupValues) hiddenIds.add(idOf(value));
	}
	if (hiddenIds.size === 0) return items;
	return items.filter((item) => !!item.groupValues || !hiddenIds.has(item.id));
};
